/**
 * Pure helpers for the shared <SkillEditor scope> component (PR-D).
 *
 * - `EMPTY_DRAFT` — initial draft shape used when opening "New skill"
 *   on the tenant adapter.
 * - `EMPTY_SYSADMIN_DRAFT` — adds the three JSON-text mirror fields
 *   (toolDependenciesText / variablesText / examplesText) that sysadmin
 *   uses instead of the tenant's typed editors.
 * - `buildTenantCreate/UpdateBody` — tenant CreateStudioSkillInput shape.
 * - `buildSysadminCreate/UpdateBody` — parses the sysadmin JSON-text
 *   fields and builds the Partial<SystemSkill> body. Returns a tagged
 *   union so the call-site can surface parse errors.
 * - `rowScope` — tri-state classifier for tenant rows (system / cloned /
 *   tenant) used by the scope filter chips.
 * - `normalizeVariablesFromHelper` — coerces the loose Skill Helper LLM
 *   args into the StudioSkillVariable shape.
 *
 * Per project pattern (see prompt-editor-shared/PromptEditor.helpers.ts
 * header): pure logic only; tests assert on these without any DOM / RTL.
 */
import type { StudioSkillVariable } from '@/lib/use-studio-skills';
import type {
  SkillDraft,
  NormalizedSkill,
  SerializeResult,
} from './SkillEditor.types';

// ─── Initial drafts ────────────────────────────────────────────────────────

export const EMPTY_DRAFT: SkillDraft = {
  slug: '',
  displayName: '',
  description: '',
  instructions: '',
  toolDependencies: [],
  variables: [],
  category: 'general',
  isActive: true,
};

/**
 * Sysadmin draft adds JSON-text mirrors. The sysadmin pre-refactor form
 * stored variables / examples / toolDependencies as raw JSON strings
 * (free-form editor); preserved for parity. The typed `variables` and
 * `toolDependencies` arrays stay empty on sysadmin — they're never read
 * by the sysadmin save path.
 */
export const EMPTY_SYSADMIN_DRAFT: SkillDraft = {
  ...EMPTY_DRAFT,
  toolDependenciesText: '[]',
  variablesText: '[]',
  examplesText: '[]',
};

// ─── Tri-state scope classifier (tenant chips) ─────────────────────────────

/**
 * Tenant-only: classify a skill row into one of three buckets.
 *
 *   system  — sysadmin-authored row visible to this tenant (read-only)
 *   cloned  — tenant-owned row that originated from a clone-from-system
 *   tenant  — tenant-owned original row
 */
export function rowScope(
  s: Pick<NormalizedSkill, 'scope' | 'clonedFromSystemId'>,
): 'system' | 'cloned' | 'tenant' {
  if (s.scope === 'system') return 'system';
  if (s.clonedFromSystemId) return 'cloned';
  return 'tenant';
}

// ─── Tenant CREATE body ────────────────────────────────────────────────────

/**
 * Build the tenant /studio-skills POST body from a draft. Mirrors the
 * pre-refactor handleSave() body construction (Phase 5B (#150)).
 */
export function buildTenantCreateBody(draft: SkillDraft): {
  name: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies: string[];
  variables: StudioSkillVariable[];
  category: string;
  isActive: boolean;
} {
  return {
    name: draft.slug,
    displayName: draft.displayName,
    description: draft.description,
    instructions: draft.instructions,
    toolDependencies: draft.toolDependencies,
    variables: draft.variables,
    category: draft.category,
    isActive: draft.isActive,
  };
}

// ─── Tenant UPDATE body ────────────────────────────────────────────────────

/**
 * Build the tenant /studio-skills/:id PATCH body. Slug intentionally
 * omitted (immutable post-create).
 */
export function buildTenantUpdateBody(draft: SkillDraft): {
  name: string;
  displayName: string;
  description: string;
  instructions: string;
  toolDependencies: string[];
  variables: StudioSkillVariable[];
  category: string;
  isActive: boolean;
} {
  // Note: the pre-refactor tenant update body still sends `name` — but
  // the server treats it as a no-op (slug is the unique key). Preserved
  // exactly for behavioural parity.
  return {
    name: draft.slug,
    displayName: draft.displayName,
    description: draft.description,
    instructions: draft.instructions,
    toolDependencies: draft.toolDependencies,
    variables: draft.variables,
    category: draft.category,
    isActive: draft.isActive,
  };
}

// ─── Sysadmin slug validation (pre-save guard) ─────────────────────────────

export const SYSADMIN_SLUG_RE = /^[a-z0-9_.-]+$/;

/**
 * Sysadmin pre-save guard. Mirrors the pre-refactor handleSave guard:
 * - slug required + matches `[a-z0-9_.-]+`
 * - displayName, description, instructions all required
 *
 * Returns null on success, otherwise the toast.error message.
 */
export function validateSysadminDraft(draft: SkillDraft): string | null {
  if (!draft.slug.trim() || !SYSADMIN_SLUG_RE.test(draft.slug.trim())) {
    return 'Slug must be lowercase alphanumeric with dots/dashes/underscores.';
  }
  if (
    !draft.displayName.trim() ||
    !draft.description.trim() ||
    !draft.instructions.trim()
  ) {
    return 'Display name, description, and instructions are all required.';
  }
  return null;
}

// ─── Sysadmin CREATE body ──────────────────────────────────────────────────

/**
 * Build the sysadmin /admin/platform/system-catalog/skills POST body
 * from a draft. Parses the JSON-text mirrors (variables / examples /
 * toolDependencies) and validates they're arrays.
 *
 * Returns a tagged union so the caller can toast parse errors back to
 * the user without throwing.
 */
export function buildSysadminCreateBody(draft: SkillDraft): SerializeResult {
  const v = validateSysadminDraft(draft);
  if (v !== null) return { ok: false, message: v };

  let toolDeps: string[];
  let variables: unknown[];
  let examples: unknown[];
  try {
    const parsed = JSON.parse(draft.toolDependenciesText ?? '[]');
    if (!Array.isArray(parsed)) {
      return {
        ok: false,
        message:
          'toolDependencies invalid: toolDependencies must be a JSON array of strings',
      };
    }
    toolDeps = parsed.map((x) => String(x));
  } catch (e) {
    return {
      ok: false,
      message: `toolDependencies invalid: ${(e as Error).message}`,
    };
  }
  try {
    const parsed = JSON.parse(draft.variablesText ?? '[]');
    if (!Array.isArray(parsed)) {
      return {
        ok: false,
        message: 'variables invalid: variables must be a JSON array',
      };
    }
    variables = parsed;
  } catch (e) {
    return {
      ok: false,
      message: `variables invalid: ${(e as Error).message}`,
    };
  }
  try {
    const parsed = JSON.parse(draft.examplesText ?? '[]');
    if (!Array.isArray(parsed)) {
      return {
        ok: false,
        message: 'examples invalid: examples must be a JSON array',
      };
    }
    examples = parsed;
  } catch (e) {
    return {
      ok: false,
      message: `examples invalid: ${(e as Error).message}`,
    };
  }

  return {
    ok: true,
    body: {
      slug: draft.slug.trim(),
      displayName: draft.displayName.trim(),
      description: draft.description.trim(),
      instructions: draft.instructions,
      toolDependencies: toolDeps,
      variables,
      examples,
      category: draft.category.trim() || 'general',
      isActive: draft.isActive,
    },
  };
}

// ─── Sysadmin UPDATE body ──────────────────────────────────────────────────

/**
 * Build the sysadmin PATCH body. Slug omitted from the wire format
 * (immutable post-create) — matches the pre-refactor `const { slug,
 * ...rest } = body` destructuring.
 */
export function buildSysadminUpdateBody(draft: SkillDraft): SerializeResult {
  const result = buildSysadminCreateBody(draft);
  if (!result.ok) return result;
  // Strip the slug from the create body.
  const { slug: _unused, ...rest } = result.body as { slug: string } & Record<
    string,
    unknown
  >;
  void _unused;
  return { ok: true, body: rest };
}

// ─── Variable coercion (Skill Helper LLM → StudioSkillVariable[]) ─────────

/**
 * Coerce the loose array shape from the Skill Helper LLM (via the
 * `set_variables` tool call) into the strict StudioSkillVariable shape.
 *
 * Tenant adapter only — sysadmin route never wired the Skill Helper.
 */
export function normalizeVariablesFromHelper(
  vars: unknown,
): StudioSkillVariable[] {
  if (!Array.isArray(vars)) return [];
  return vars.map((raw): StudioSkillVariable => {
    const v = (raw ?? {}) as Record<string, unknown>;
    const type =
      v.type === 'number' || v.type === 'boolean' ? v.type : 'string';
    return {
      name: String(v.name ?? ''),
      description: String(v.description ?? ''),
      type,
      required: !!v.required,
    };
  });
}

// ─── Helper-mutation → draft patch (#1095 fix) ─────────────────────────────

/**
 * Apply one Skill Helper mutation tool call to the in-sheet draft.
 *
 * #1095 — Pre-fix the Accept handler called multiple `setDraft({ ...draft,
 * foo })` statements in sequence inside one event-handler tick. Every
 * `setDraft` captured the SAME stale `draft` value from the React
 * closure, so only the LAST `setDraft`'s field landed and every other
 * field was silently overwritten back to its pre-Accept value. With
 * `displayName` left blank the "Create skill" button stayed disabled
 * (the save guard requires slug + displayName + description +
 * instructions), so the helper appeared to do nothing.
 *
 * Two-part fix:
 *   1. Coalesce all field updates into ONE `Partial<SkillDraft>` patch
 *      the caller applies via a single functional setState. Logic is
 *      pure + unit-tested below.
 *   2. The Skill Helper backend emits `set_metadata({ name, slug?,
 *      description?, whenToUse? })`. The skill schema says `name` is
 *      the human-readable name; the slug is derived. Pre-fix mapped
 *      `a.name → draft.slug` and never populated `displayName`. We now
 *      ALSO write `a.name` to `displayName` so the Create button can
 *      enable from a single `set_metadata` card. If the helper does
 *      send an explicit `slug` we honour it; otherwise we seed `slug`
 *      from `a.name` only when the existing draft slug is blank (the
 *      caller can apply a slugifier later if needed — we don't try to
 *      slugify here so a typed `slug` field stays predictable).
 *
 * Returns a partial patch. Unknown tool names → empty patch (no-op).
 * `add_tool_dep` / `remove_tool_dep` require the existing draft's
 * `toolDependencies` to compute the new array, so those are handled by
 * the caller via a functional setState (see SkillEditor.tsx).
 *
 * @param toolName  - one of `set_metadata` | `set_instructions` | `set_variables`
 * @param args      - the helper's tool args (validated server-side via Zod)
 */
export function applySkillHelperMutation(
  toolName: string,
  args: Record<string, unknown>,
  // `instructions` is optional so existing 2-field callers/tests still type —
  // set_guardrails reads it (defaulting to '') to fold into the body.
  existingDraft: Pick<SkillDraft, 'slug' | 'displayName'> & { instructions?: string },
): Partial<SkillDraft> {
  switch (toolName) {
    case 'set_metadata': {
      const patch: Partial<SkillDraft> = {};
      if (args.name != null) {
        const name = String(args.name);
        // Always populate displayName from `name` — the helper emits
        // "name" semantically meaning the human-readable name.
        patch.displayName = name;
        // Seed slug from name ONLY when the existing draft slug is
        // blank AND the helper did not supply an explicit slug below.
        // The user can edit slug afterwards; we don't slugify here.
        if (args.slug == null && !existingDraft.slug.trim()) {
          patch.slug = name;
        }
      }
      if (args.slug != null) {
        patch.slug = String(args.slug);
      }
      if (args.description != null) {
        patch.description = String(args.description);
      }
      // whenToUse is part of the schema but the SkillDraft has no
      // matching field — folding it into description preserves the
      // helper's guidance without losing data. Concatenate with a
      // separator only when description is also present.
      if (args.whenToUse != null) {
        const whenToUse = String(args.whenToUse);
        if (patch.description) {
          patch.description = `${patch.description}\n\nWhen to use: ${whenToUse}`;
        } else if (!patch.description && args.description == null) {
          // No description from helper AND no description fold-in:
          // surface whenToUse as the description so the field isn't
          // empty after Accept.
          patch.description = `When to use: ${whenToUse}`;
        }
      }
      return patch;
    }
    case 'set_instructions': {
      if (args.instructionsMd != null) {
        return { instructions: String(args.instructionsMd) };
      }
      return {};
    }
    case 'set_variables': {
      if (Array.isArray(args.variables)) {
        return {
          variables: normalizeVariablesFromHelper(
            args.variables,
          ) as StudioSkillVariable[],
        };
      }
      return {};
    }
    case 'set_guardrails': {
      // SkillDraft has no `guardrails` field and there is no guardrails column
      // on studio_skills/skills — so the skill_helper's dedicated guardrails
      // step (4-9 affirmative safety invariants + prohibitions) used to be
      // discarded on every Accept. Fold a NON-EMPTY guardrails array into the
      // persisted `instructions` markdown as a "## Guardrails" section:
      // `instructions` is the ONE field injected into the agent system prompt
      // at runtime (skill_refs → skills.instructions), exactly where safety
      // invariants belong. An empty array stays a no-op so the existing test
      // (applySkillHelperMutation('set_guardrails', { guardrails: [] }) → {})
      // remains green.
      const raw = args.guardrails;
      const items = (Array.isArray(raw) ? raw : [])
        .map((x) => {
          if (typeof x === 'string') return x.trim();
          if (x && typeof x === 'object') {
            const o = x as Record<string, unknown>;
            return String(o.rule ?? o.text ?? o.description ?? o.guardrail ?? '').trim();
          }
          return '';
        })
        .filter((x) => x.length > 0);
      if (items.length === 0) return {};
      const section = `## Guardrails\n${items.map((x) => `- ${x}`).join('\n')}`;
      const base = (existingDraft.instructions ?? '').trimEnd();
      return { instructions: base ? `${base}\n\n${section}` : section };
    }
    default:
      return {};
  }
}
